import { SemanticGradeTag } from 'components/InstructorSubmissions/SemanticGradeDisplay';
import { useApp } from 'contexts/app';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AccordionRubric,
    Avatar,
    Box,
    Button,
    MenuItem,
    Select,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import {
    getLatestConfirmedId,
    getLatestConfirmedTimestamp,
    getRubricMaxPoints,
    getUserPreferredFullName,
    isValidRate,
} from 'utils/helper';
import {
    CompletionStatus,
    getEffectiveCompletionStatus,
    isUnscoredRubric,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';

/**
 * @param {Object} props
 * @param {boolean} [props.showSuggestedTag=true] Caller-level override for suggested tags. Tags only render when this is true and `assignment.features.suggestion_tags` is not false.
 */
const RubricRenderer = ({
    disabled = false,
    showGraded = true,
    showGradingExplanationInput,
    showSuggestedTag: _showSuggestedTag = true,
    assignment,
    currentSubmission,
    selectedFileId,
    setSelectedFileId,
    locations = [],
    form,
    setForm,
    gradeOverrides = {},
    setGradeOverrides,
    selectedCriterionId,
    setSelectedCriterionId,
    setShowFailedDialog,
    feedbackRegenerationReasons = {},
    handleCriterionNavigation,
    hideCriterionNavigation = false,
}) => {
    const { accountUsers } = useApp();
    const { t } = useTranslation('assignment');
    const isUnscored = isUnscoredRubric(assignment);
    const showsCompletionStatus = isUnscored && usesCompleteIncompleteGrading(assignment);
    const completionStatus = getEffectiveCompletionStatus({
        criteria: assignment?.rubric?.criteria,
        selections: form?.criteria,
        status: form?.status,
    });
    const confirmedCompletionStatus = currentSubmission.grading
        ? getEffectiveCompletionStatus({
              criteria: assignment?.rubric?.criteria,
              selections: currentSubmission.grading.criteria,
              status: currentSubmission.grading.status,
          })
        : null;
    const isCompletionStatusUnconfirmed =
        showsCompletionStatus &&
        Boolean(completionStatus) &&
        (!currentSubmission.grading || completionStatus !== confirmedCompletionStatus);

    const locationData = useMemo(() => {
        return {
            locations,
            files: currentSubmission?.files ?? [],
        };
    }, [locations, currentSubmission?.files]);
    const isRubricTurnedOn = assignment?.features?.rubric ?? assignment?.features?.assignment_rubric ?? true;
    const showSuggestedTag = _showSuggestedTag && assignment?.features?.suggestion_tags !== false;

    const rubric = useMemo(
        () =>
            assignment.rubric.criteria.map((criterion) => {
                const justification = currentSubmission.ai?.grading?.[criterion.id]?.justification;
                const suggestedOptionId = currentSubmission.ai?.grading?.[criterion.id]?.optionId;
                const gradingInstructions =
                    assignment.ai_grading_settings?.criteria?.[criterion.id]?.grading_instructions;
                const needsFeedbackRegeneration = Boolean(feedbackRegenerationReasons[criterion.id]?.length);
                if (!suggestedOptionId) {
                    return {
                        ...criterion,
                        gradingInstructions,
                        is_rubric_turned_on: isRubricTurnedOn,
                        needsFeedbackRegeneration,
                    };
                }
                return {
                    ...criterion,
                    justification,
                    needsFeedbackRegeneration,
                    options: criterion.options.map((option) => ({
                        ...option,
                        suggested: showSuggestedTag ? option.id === suggestedOptionId : false,
                    })),
                    gradingInstructions,
                    is_rubric_turned_on: isRubricTurnedOn,
                };
            }),
        [assignment, currentSubmission.ai?.grading, feedbackRegenerationReasons, isRubricTurnedOn, showSuggestedTag]
    );

    const isDirty = useMemo(() => {
        return Boolean(Object.keys(form?.criteria ?? {}).length) || Boolean(Object.keys(gradeOverrides).length);
    }, [form?.criteria, gradeOverrides]);

    const totalScore = useMemo(() => {
        if (isUnscored) return '';
        if (!currentSubmission.ai?.grading && !isDirty) return '';
        if (!form) return '';
        if (Object.keys(gradeOverrides).includes('total')) return gradeOverrides.total;

        return rubric
            .reduce((acc, criterion) => {
                const doesOverrideExist = Object.keys(gradeOverrides).includes(criterion.id);
                if (doesOverrideExist && isValidRate(gradeOverrides[criterion.id])) {
                    return acc + Number(gradeOverrides[criterion.id]);
                }
                const selectedOptionId = form.criteria[criterion.id];
                if (selectedOptionId) {
                    return acc + Number(criterion.options.find(({ id }) => id === selectedOptionId).rate);
                }
                return acc + Number(criterion.options.find(({ suggested }) => suggested)?.rate || 0);
            }, 0)
            .toString();
    }, [currentSubmission?.ai?.grading, form, gradeOverrides, isDirty, isUnscored, rubric]);

    const maxTotalScore = useMemo(() => {
        if (isUnscored) return null;
        return getRubricMaxPoints(rubric);
    }, [isUnscored, rubric]);

    const gradedByOwnerAccountId = getLatestConfirmedId(currentSubmission.sessions);
    const gradedByAccountUser = accountUsers.find(
        ({ owner_account_id }) => owner_account_id === gradedByOwnerAccountId
    );
    const gradedByTimeStamp = getLatestConfirmedTimestamp(currentSubmission.sessions);

    if (!form) return null;

    return (
        <Stack overflow={'auto'}>
            <BackgroundStack spacing={1}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('rubric.title-rubric', 'Rubric')}
                    </Text>
                    {showsCompletionStatus ? (
                        <CompletionStatusSelect
                            disabled={disabled}
                            isUnconfirmed={isCompletionStatusUnconfirmed}
                            status={completionStatus}
                            t={t}
                            onChange={(status) => {
                                setForm((current) => ({ ...current, status }));
                            }}
                        />
                    ) : (
                        !isUnscored && (
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <Text>{t('rubric.total-points', 'Total Points')}: </Text>
                                <OverrideTextInput
                                    aria-label={'total points'}
                                    disabled={disabled}
                                    error={
                                        !isDirty
                                            ? false
                                            : !isValidRate(totalScore) ||
                                              Number(totalScore) < 0 ||
                                              Number(totalScore) > maxTotalScore
                                    }
                                    isSuggestedScore={
                                        !Object.keys(gradeOverrides).length && !Object.keys(form.criteria).length
                                    }
                                    size={'small'}
                                    value={isRubricTurnedOn ? totalScore : ''}
                                    onInput={(e) =>
                                        setGradeOverrides((current) => ({ ...current, total: e.target.value }))
                                    }
                                />
                                <Text>/ {maxTotalScore}</Text>
                            </Stack>
                        )
                    )}
                </Stack>
                {gradedByAccountUser && (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text color={'light'} weight={'regular'}>
                            {t('graded-by', 'Graded by')}:
                        </Text>
                        <Avatar user={gradedByAccountUser} />
                        <Text>{getUserPreferredFullName(gradedByAccountUser)}</Text>
                        <Text color={'light'}>{t('common:ui.format-date-time', { val: gradedByTimeStamp })}</Text>
                    </Stack>
                )}
            </BackgroundStack>
            <Stack sx={{ p: 3 }} spacing={4}>
                {currentSubmission.is_failed && (
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            {t(
                                'grading-section.unable-to-generate',
                                'We were unable to generate grades for this assignment. You can try to regenerate, or grade the submission manually. You must select grades and add feedback to confirm.'
                            )}
                        </Text>
                        <Button variant={'borderless'} onClick={() => setShowFailedDialog(true)}>
                            {t('common:ui.learn-more', 'Learn more')}
                        </Button>
                    </Stack>
                )}
                <AccordionRubric
                    hasBeenGraded={Boolean(gradedByOwnerAccountId)}
                    isUnscored={isUnscored}
                    showGraded={showGraded}
                    showGradingExplanationInput={
                        showGradingExplanationInput ??
                        ['custom', 'rubric', 'system'].includes(currentSubmission.ai?.feedback?.feedback_type)
                    }
                    rubric={rubric}
                    disabled={disabled}
                    form={form}
                    setForm={setForm}
                    gradeOverrides={gradeOverrides}
                    setGradeOverrides={setGradeOverrides}
                    selectedCriterionId={selectedCriterionId}
                    setSelectedCriterionId={setSelectedCriterionId}
                    handleCriterionNavigation={handleCriterionNavigation}
                    hideCriterionNavigation={hideCriterionNavigation}
                    locationData={locationData}
                    selectedFileId={selectedFileId}
                    setSelectedFileId={setSelectedFileId}
                />
            </Stack>
        </Stack>
    );
};

const CompletionStatusSelect = ({ disabled, isUnconfirmed, status, t, onChange }) => {
    return (
        <StatusSelectContainer>
            <StatusSelect
                aria-label={t('rubric.status.label', 'Submission status')}
                disabled={disabled}
                displayEmpty
                size={'small'}
                value={status ?? ''}
                renderValue={(value) =>
                    value ? (
                        <SemanticGradeTag value={value} />
                    ) : (
                        <Text color={'light'} variant={'p3'}>
                            {t('rubric.status.select', 'Select status')}
                        </Text>
                    )
                }
                onChange={(event) => onChange(event.target.value)}
            >
                {Object.values(CompletionStatus).map((value) => (
                    <MenuItem key={value} value={value}>
                        <SemanticGradeTag value={value} />
                    </MenuItem>
                ))}
            </StatusSelect>
            {isUnconfirmed && !disabled && <StatusDot aria-hidden={'true'} />}
        </StatusSelectContainer>
    );
};

const StatusSelectContainer = styled(Box)(() => ({
    position: 'relative',
}));

const StatusSelect = styled(Select)(({ theme }) => ({
    width: '145px',
    height: '36px',
    border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
    borderRadius: '16px',
    '& > div': {
        minHeight: '24px',
        padding: theme.spacing(1.5, 8, 1.5, 3),
    },
    '& > svg': {
        right: theme.spacing(3),
        fontSize: '1rem',
    },
}));

const StatusDot = styled(Box)(({ theme }) => ({
    position: 'absolute',
    width: '12px',
    height: '12px',
    top: '-1px',
    right: '1px',
    borderRadius: '50%',
    background: theme.palette.ensightful.error[600],
}));

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
        position: 'sticky',
        top: 0,
        zIndex: 1,
    };
});

const OverrideTextInput = styled(TextInput)(({ theme, isSuggestedScore }) => {
    return {
        width: '55px',
        '& > .Mui-disabled > input': {
            color: theme.palette.ensightful.neutral[900],
            '-webkitTextFillColor': 'unset',
        },
        ...(isSuggestedScore && {
            '& input.MuiInputBase-input': {
                color: theme.palette.ensightful.primary[500],
            },
        }),
    };
});

export default RubricRenderer;
